import React, { useEffect, useState } from "react"
import axios from "axios"
import { api } from "../../utils/variables"
import "./CustomInsight.css"
import { useNavigate } from "react-router-dom"
import AppRankCard from "./AppRankCard"
import AppCardSkeleton from "./AppCardSkeleton"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl } from "../../utils/seoUtils"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"


export default function TopReviewed() {
  const { trackEvent } = useAnalytics()
  const [platform, changePlatform] = useState("all")
  const [topReviewed, setTopReviewed] = useState([])
  const [topReviewedDetails, setTopReviewedDetails] = useState([])
  const [sortedTopReviewed, setSortedTopReviewed] = useState([])
  const [isSorted, setIsSorted] = useState(false) // Track sorting state
  const [itemsToShow, setItemsToShow] = useState(6) // Initially show 6 items
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(false)
  const isReleasedMeta = sessionStorage.getItem("releaseMeta")
  const navigate = useNavigate()

  const handleCardClick = (app) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_APP_SELECTED, { app_id: app?.item_id, app_name: app?.name })
    navigate(`/xr/app/${(app?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${app?.item_id}`, {
      state: { app_id: app?.item_id, data: app, source_surface: "custom_insight_top_reviewed" }
    })
  }

  useEffect(() => {
    setLoading(true)
    fetchAll()
  }, [])

  const fetchAll = () => {
    try {
      fetchTopReviewed()
    } finally {
    }
  }

  const fetchTopReviewed = async () => {
    try {
      const res = await axios.get(`${api}/tracker/dashboard/top_reviewed`)
      setTopReviewed(res.data.data)
      setTopReviewedDetails(res.data.details)
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_REVIEWED, app_count: Array.isArray(res.data.data) ? res.data.data.length : undefined })
    } catch (error) {
      console.error("Error fetching in releases:", error)
    } finally {
      setLoading(false)
    }
  }

  const handlePlatformChange = platform => {
    changePlatform(platform) // Update the platform

    const platformReleaseGames =
      platform !== "all"
        ? topReviewed.filter(game => game.platform === platform)
        : topReviewed // If "all", return all games
    setSortedTopReviewed(platformReleaseGames)
  }

  const handleSort = event => {
    const sortBy = event.target.value
    let sortedRelease = []
    if (sortBy === "revenue") {
      sortedRelease = [...topReviewed].sort((a, b) => b.revenue - a.revenue)
    } else if (sortBy === "downloads") {
      sortedRelease = [...topReviewed].sort((a, b) => b.downloads - a.downloads)
    } else if (sortBy === "ratings") {
      sortedRelease = [...topReviewed].sort(
        (a, b) => b.rating["average"] - a.rating["average"]
      )
    } else {
      sortedRelease = topReviewed
    }
    setSortedTopReviewed(sortedRelease)
    setIsSorted(true)
  }

  useEffect(() => {
    if (showAll) setItemsToShow(topReviewed.length)
    else setItemsToShow(6)
  }, [showAll]) // Re-run when "showAll" changes or data length changes

  const slides = isSorted
    ? sortedTopReviewed.slice(0, itemsToShow)
    : topReviewed.slice(0, itemsToShow)

  const formatNumber = num => {
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + " M"
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + " K"
    }
    return num || 0
  }

  return (
    <React.Fragment>
      <SEO
        title={PAGE_SEO.topReviewed.title}
        description={PAGE_SEO.topReviewed.description}
        keywords={PAGE_SEO.topReviewed.keywords}
        canonical={generateCanonicalUrl('/top-reviewed')}
        robots={PAGE_SEO.topReviewed.robots}
        pageType="topReviewed"
      />
      <div className="page-content top-improved-container">
        <div className="top-header-section">
          <div className="title-area">
            <h1 className="top-improved-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              Top Reviewed
            </h1>
          </div>
          <div className="filters-area">
            <div className="filter-group">
              <label className="filter-label">Sort by</label>
              <select className="filter-select" onChange={handleSort}>
                <option value="">Select</option>
                <option value="revenue">Revenue</option>
                <option value="downloads">Downloads</option>
                <option value="ratings">Ratings</option>
              </select>
            </div>
            <div className="filter-group">
              <label className="filter-label">Date</label>
              <input
                className="filter-date"
                type="date"
                defaultValue={new Date().toISOString().split("T")[0]}
                max={new Date().toISOString().split("T")[0]}
              />
            </div>
          </div>
        </div>

        <div className="top-improved-description-section">
          <p className="top-improved-description">
            Unlock powerful analytics with Custom Insights, your ultimate tool
            for tracking top app improvements, revenue trends, and key
            performance metrics. Stay ahead of the competition with real-time
            data, actionable insights, and in-depth reports tailored for app
            developers, investors, and businesses.
          </p>
        </div>

        <div className="app-cards-container">
          {loading ? (
            <AppCardSkeleton count={6} />
          ) : (
            <>
              {slides.map((app, index) => (
                <AppRankCard
                  key={app?.item_id || index}
                  app={app}
                  fallbackPlatformId={platform !== "all" ? platform : null}
                  onClick={() => handleCardClick(app)}
                  secondMetricLabel="Revenue"
                  secondMetricValue={<span className="metric-value">${app?.revenue ? formatNumber(app.revenue) : "5.3 M"}</span>}
                />
              ))}
            </>
          )}
        </div>

        {!loading && !showAll && topReviewed.length > 6 && (
          <div className="see-more-container">
            <button
              className="see-more-button"
              onClick={() => setShowAll(true)}
            >
              See More
            </button>
          </div>
        )}

        {!loading && showAll && topReviewed.length > 6 && (
          <div className="see-more-container">
            <button
              className="see-more-button"
              onClick={() => setShowAll(false)}
            >
              See Less
            </button>
          </div>
        )}
      </div>
    </React.Fragment>
  )
}
